import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from './api.service';
import { apiEndPoints, apiMethods, common, userTypes } from '@constants/index';
import { ILoginResponse, ILogin, ICurrentUser, IPriceWelcomeStatusResponse } from '@core/models';
import { CookieService } from 'ngx-cookie-service';

@Injectable()
export class SessionService {
  private readonly currentUserSubject: BehaviorSubject<ICurrentUser>;
  public currentUser$: Observable<ICurrentUser>;

  private cookieOptions = {
    domain: window.location.hostname,
    secure: false,
    path: '/',
    expires: new Date(
      new Date().setTime(new Date().getTime() + common.cookiesExpriesTime)
    ),
    sameSite: 'None'
  };
  constructor(
    private readonly http: ApiService,
    private readonly cookieService: CookieService
  ) {
    this.currentUserSubject = new BehaviorSubject<ICurrentUser>(this.getCurrentUser());
    this.currentUser$ = this.currentUserSubject.asObservable();
  }
  public sendLogin(loginData): Observable<ILoginResponse> {
    return this.http.invoke({
      path: `${apiEndPoints.accounts}${apiEndPoints.login}`,
      method: apiMethods.post,
      body: loginData,
    });
  }
  public logout(): Observable<any> {
    return this.http.invoke({
      path: `${apiEndPoints.accounts}${apiEndPoints.logout}`,
      method: apiMethods.post,
    });
  }
  public sendPricePlanSeen(): Observable<IPriceWelcomeStatusResponse> {
    const currentUser = this.getCurrentUser();
    const postData = {
      pricing_plan_seen: true,
      welcome_screen_seen: true
    };
    return this.http.invoke({
      path: `${apiEndPoints.accounts}${apiEndPoints.pricePlanSeen}/${currentUser.userId}`,
      method: apiMethods.patch,
      body: postData
    });
  }
  public createSession(loginData: ILogin): void {
    if (loginData && loginData.token) {
      this.setCookie('token', JSON.stringify(loginData.token));
    }
    const {
      id,
      full_name,
      profile_picture,
      phone_number,
      email,
      email_verified,
      ca_charge,
      pricing_plan_seen,
      welcome_screen_seen,
      address,
      latitude,
      longitude,
      user_type
    } = loginData;
    const currentUser = {
      userId: id,
      fullName: full_name,
      profilePicture: profile_picture,
      phoneNumber: phone_number,
      email,
      emailVerified: email_verified,
      caCharge: ca_charge,
      pricingPlanSeen: pricing_plan_seen,
      welcomeScreenSeen: welcome_screen_seen,
      address,
      latitude,
      longitude,
      userType: user_type
    };
    this.updateCurrentUser(currentUser);
  }
  public isAuthenticate(): boolean {
    const token = this.getCookie('token');
    if (token) {
      return 'access_token' in JSON.parse(token);
    }
    return false;
  }
  public getCurrentUser(): ICurrentUser {
    const currentUser = this.getCookie('user');
    return currentUser && JSON.parse(currentUser);
  }
  public getAccessToken(): string{
    const token = this.getCookie('token');
    return token && JSON.parse(token).access_token ? JSON.parse(token).access_token : '';
  }
  public setPricingPlanSeen(status: boolean): void {
    const user = this.getCurrentUser();
    user.pricingPlanSeen = status;
    this.updateCurrentUser(user);
  }
  public getPricePlanSeen(): boolean {
    const user = this.getCurrentUser();
    return user.pricingPlanSeen;
  }
  public updateCurrentUser(currentUser: ICurrentUser): void {
    this.setCookie('user', JSON.stringify(currentUser));
    this.currentUserSubject.next(currentUser);
  }
  public setCookie(key: string, value: string): void {
     this.cookieService.set(
      key,
      value,
      this.cookieOptions.expires,
      this.cookieOptions.path,
      this.cookieOptions.domain,
      this.cookieOptions.secure,
      'None'
    );
  }
  public getCookie = (key: string) => this.cookieService.get(key);
  public clearCookie(key: string): void {
    this.cookieService.delete(key);
  }
  public clearAllCookies(): void {
    this.cookieService.deleteAll(
      this.cookieOptions.path,
      this.cookieOptions.domain
    );
  }
  public isAdmin(): boolean {
    return this.getCurrentUser().userType === userTypes.admin;
  }
}
